import { getIdToken } from './auth';

/** Origin for REST `/api/v1/billing/...` calls (matches embedded checkout). */
export function getBillingApiOrigin(): string {
  const configuredApiUrl = process.env.REACT_APP_API_URL;
  if (configuredApiUrl) {
    try {
      const apiUrl = new URL(configuredApiUrl);
      return `${apiUrl.protocol}//${apiUrl.host}`;
    } catch {
      // fall through to hostname-based derivation
    }
  }

  if (typeof window !== 'undefined') {
    const hostParts = window.location.hostname.split('.');
    if (hostParts.length >= 2) {
      const domain = hostParts.slice(-2).join('.');
      return `${window.location.protocol}//api.${domain}`;
    }
    return window.location.origin;
  }

  return '';
}

export type BillingOrderStatus = 'pending' | 'paid' | 'failed' | 'refunded';

export type BillingOrderLine = {
  internal_sku: string;
  display_name: string;
  charge_type: string;
  quantity: number;
  unit_amount_cents: number;
  stripe_price_id: string | null;
};

export type BillingOrder = {
  id: number;
  status: string;
  currency: string;
  total_amount_cents: number;
  stripe_payment_intent_id: string | null;
  stripe_receipt_url: string | null;
  fulfilled_at: string | null;
  is_fulfilled?: boolean;
  fulfillment_error?: string | null;
  onboarding_organization_id?: number | null;
  created_at: string | null;
  updated_at: string | null;
  lines: BillingOrderLine[];
};

export function billingOrderIsFulfilled(order: BillingOrder): boolean {
  if (typeof order.is_fulfilled === 'boolean') {
    return order.is_fulfilled;
  }
  return Boolean(order.fulfilled_at);
}

/** Laravel paginator slice returned by GET /api/v1/billing/orders */
export type BillingOrdersListResponse = {
  current_page: number;
  data: BillingOrder[];
  first_page_url: string | null;
  from: number | null;
  last_page: number;
  last_page_url: string | null;
  links?: {
    url: string | null;
    label: string;
    active: boolean;
  }[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
};

export type FetchBillingOrdersParams = {
  page?: number;
  perPage?: number;
  status?: BillingOrderStatus;
};

export function formatBillingMoney(amountCents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export async function fetchBillingOrders(
  params: FetchBillingOrdersParams = {}
): Promise<BillingOrdersListResponse> {
  const token = getIdToken();
  const apiOrigin = getBillingApiOrigin();
  const search = new URLSearchParams();
  if (typeof params.page === 'number') {
    search.set('page', String(params.page));
  }
  if (typeof params.perPage === 'number') {
    search.set('per_page', String(params.perPage));
  }
  if (params.status) {
    search.set('status', params.status);
  }
  const qs = search.toString();
  const url = `${apiOrigin}/api/v1/billing/orders${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const errorMessage = body?.message || body?.error || '';
    const fallback =
      response.status === 401
        ? 'Sign in required.'
        : response.status === 422
          ? 'Invalid request.'
          : `Orders request failed (${response.status})`;
    const error = new Error(errorMessage || fallback);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return response.json() as Promise<BillingOrdersListResponse>;
}

/** Self-serve onboarding bundle SKU (Backpack / billing catalog). */
export const ONBOARDING_ORGANIZATION_BUNDLE_IDENTIFIER = 'ONBOARDING_ORGANIZATION_BUNDLE';

export const FULFILLMENT_HANDLER_ONBOARDING_ORGANIZATION = 'onboarding_organization_setup';

/** Shape from GET /api/v1/billing/catalog/item (bundle mode item). */
export type BillingCatalogItem = {
  billing_catalog_item_id: number;
  internal_sku: string;
  display_name: string;
  description: string;
  charge_type: string;
  fulfillment_handler_key: string;
  credits_quantity: number;
  min_quantity: number;
  max_quantity: number;
  quantity_step: number;
  unit_amount_cents: number | null;
  has_intro_pricing?: boolean;
  first_period_unit_amount_cents?: number | null;
  provision_profile_key?: string | null;
  currency: string;
  price_available: boolean;
  bundle_item_sort_order: number | null;
};

export function catalogItemRequiresOrgOnboarding(item: BillingCatalogItem): boolean {
  return item.fulfillment_handler_key === FULFILLMENT_HANDLER_ONBOARDING_ORGANIZATION;
}

/** Amount due today / first period for catalog display (not checkout). */
export function catalogItemDueTodayCents(item: BillingCatalogItem): number | null {
  if (!item.price_available) {
    return null;
  }
  if (item.has_intro_pricing && item.first_period_unit_amount_cents != null) {
    return item.first_period_unit_amount_cents;
  }
  return item.unit_amount_cents;
}

export function catalogItemIsPurchasable(item: BillingCatalogItem): boolean {
  return item.price_available && catalogItemDueTodayCents(item) != null;
}

export function formatCatalogItemPriceLabel(item: BillingCatalogItem): string {
  const dueToday = catalogItemDueTodayCents(item);
  if (dueToday == null) {
    return '—';
  }
  const dueLabel = formatBillingMoney(dueToday, item.currency);
  if (
    item.charge_type === 'subscription' &&
    item.has_intro_pricing &&
    item.unit_amount_cents != null &&
    item.unit_amount_cents !== dueToday
  ) {
    return `${dueLabel} first month, then ${formatBillingMoney(item.unit_amount_cents, item.currency)}/mo`;
  }
  if (item.charge_type === 'subscription' && item.unit_amount_cents != null) {
    return `${formatBillingMoney(item.unit_amount_cents, item.currency)}/mo`;
  }
  return dueLabel;
}

export type BillingCatalogBundleResponse = {
  bundle_identifier: string;
  one_time_items: BillingCatalogItem[];
  subscription_items: BillingCatalogItem[];
};

export async function fetchBillingCatalogBundle(
  bundleIdentifier: string
): Promise<BillingCatalogBundleResponse> {
  const token = getIdToken();
  const apiOrigin = getBillingApiOrigin();
  const url = `${apiOrigin}/api/v1/billing/catalog/item?bundle_identifier=${encodeURIComponent(bundleIdentifier)}`;
  const response = await fetch(url, {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const errorMessage = body?.message || body?.error || '';
    const fallback =
      response.status === 404
        ? 'This bundle is not available for purchase.'
        : response.status === 503
          ? 'Billing is temporarily unavailable. Please try again later.'
          : response.status === 429
            ? 'Too many requests. Please wait a moment and try again.'
            : response.status === 401
              ? 'Sign in required to load products.'
              : `Catalog bundle lookup failed (${response.status})`;
    const error = new Error(errorMessage || fallback);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return response.json() as Promise<BillingCatalogBundleResponse>;
}

export type BillingChargeType = 'one_time' | 'subscription';

export type BillingPaymentIntentResponse = {
  client_secret: string;
  publishable_key: string;
  order_id: number;
  amount_cents: number;
  currency: string;
  /** From checkout API; subscription vs one-time is determined server-side from catalog. */
  charge_type?: BillingChargeType | string;
  /** Present when charge_type is subscription; optional for support/debug. */
  stripe_subscription_id?: string | null;
  /** Full recurring unit price after intro (subscription checkout). */
  recurring_unit_amount_cents?: number | null;
};

/** True when the client must run Stripe in-app confirmation (normal one-time and subscription PI path). */
export function billingCheckoutRequiresCardConfirmation(
  response: BillingPaymentIntentResponse
): boolean {
  return Boolean(response.client_secret?.trim());
}

/**
 * Rare subscription path: $0 first invoice already satisfied — no PaymentIntent to confirm in-app;
 * fulfillment is webhook-driven; poll the billing order until paid.
 */
export function billingCheckoutIsSubscriptionWithoutClientSecret(
  response: BillingPaymentIntentResponse
): boolean {
  return !billingCheckoutRequiresCardConfirmation(response) && response.charge_type === 'subscription';
}

export type CreateBillingPaymentIntentBody = {
  quantity?: number;
  onboarding_organization_name?: string;
  onboarding_organization_subdomain?: string;
} & (
  | { billing_catalog_item_id: number; internal_sku?: undefined }
  | { internal_sku: string; billing_catalog_item_id?: undefined }
);

export async function createBillingPaymentIntent(
  body: CreateBillingPaymentIntentBody
): Promise<BillingPaymentIntentResponse> {
  const token = getIdToken();
  const apiOrigin = getBillingApiOrigin();
  const response = await fetch(`${apiOrigin}/api/v1/billing/checkout/payment-intent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const resBody = await response.json().catch(() => null);
    const errorMessage = resBody?.message || resBody?.error || '';
    const error = new Error(errorMessage || `Checkout failed (${response.status})`);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return response.json() as Promise<BillingPaymentIntentResponse>;
}

export type SubdomainAvailabilityReason = 'taken' | 'invalid_format' | null;

export type SubdomainAvailabilityResponse = {
  subdomain: string;
  available: boolean;
  reason: SubdomainAvailabilityReason;
  message: string | null;
};

export async function fetchSubdomainAvailability(
  subdomain: string
): Promise<SubdomainAvailabilityResponse> {
  const token = getIdToken();
  const apiOrigin = getBillingApiOrigin();
  const url = `${apiOrigin}/api/v1/billing/onboarding/subdomain-availability?subdomain=${encodeURIComponent(subdomain.trim().toLowerCase())}`;
  const response = await fetch(url, {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const errorMessage = body?.message || body?.error || '';
    const fallback =
      response.status === 401
        ? 'Sign in required.'
        : `Subdomain check failed (${response.status})`;
    const error = new Error(errorMessage || fallback);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return response.json() as Promise<SubdomainAvailabilityResponse>;
}

export async function fetchBillingOrderById(orderId: number): Promise<BillingOrder> {
  const token = getIdToken();
  const apiOrigin = getBillingApiOrigin();
  const response = await fetch(`${apiOrigin}/api/v1/billing/orders/${orderId}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const errorMessage = body?.message || body?.error || '';
    const fallback =
      response.status === 401
        ? 'Sign in required.'
        : `Order request failed (${response.status})`;
    const error = new Error(errorMessage || fallback);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return response.json() as Promise<BillingOrder>;
}

export type PollBillingOrderOptions = {
  maxAttempts?: number;
  intervalMs?: number;
  timeoutMessage?: string;
  /** Called after each order fetch (e.g. to update polling UI copy). */
  onPoll?: (order: BillingOrder) => void;
};

export async function pollBillingOrderUntil(
  orderId: number,
  predicate: (order: BillingOrder) => boolean,
  options?: PollBillingOrderOptions
): Promise<BillingOrder> {
  const maxAttempts = options?.maxAttempts ?? 36;
  const intervalMs = options?.intervalMs ?? 2000;
  const timeoutMessage =
    options?.timeoutMessage ??
    'Timed out waiting for billing. You can refresh or check purchase history.';

  const pollOnce = async (remaining: number): Promise<BillingOrder> => {
    if (remaining <= 0) {
      throw new Error(timeoutMessage);
    }
    const order = await fetchBillingOrderById(orderId);
    if (predicate(order)) {
      return order;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, intervalMs);
    });
    return pollOnce(remaining - 1);
  };

  return pollOnce(maxAttempts);
}

const ONBOARDING_FULFILLMENT_TIMEOUT_MESSAGE =
  'Setup is taking longer than usual. If you were charged, check Billing orders or contact support.';

/**
 * Poll order after Stripe success for org onboarding: handles paid+fulfilled,
 * fulfillment_error, and failed payment without relying only on fulfilled_at.
 */
export async function pollBillingOrderForOnboardingFulfillment(
  orderId: number,
  options?: PollBillingOrderOptions
): Promise<BillingOrder> {
  const maxAttempts = options?.maxAttempts ?? 45;
  const intervalMs = options?.intervalMs ?? 2000;
  const timeoutMessage = options?.timeoutMessage ?? ONBOARDING_FULFILLMENT_TIMEOUT_MESSAGE;

  const pollOnce = async (remaining: number): Promise<BillingOrder> => {
    const order = await fetchBillingOrderById(orderId);
    options?.onPoll?.(order);
    if (order.status === 'failed') {
      throw new Error('Payment failed. Please try checkout again.');
    }
    if (order.fulfillment_error) {
      throw new Error(order.fulfillment_error);
    }
    if (order.status === 'paid' && billingOrderIsFulfilled(order)) {
      return order;
    }
    if (remaining <= 1) {
      throw new Error(timeoutMessage);
    }
    await new Promise((resolve) => {
      setTimeout(resolve, intervalMs);
    });
    return pollOnce(remaining - 1);
  };

  return pollOnce(maxAttempts);
}
